'use client';

import { useMemo, useState } from 'react';
import {
  ANIMATION_DIRECTION_OPTIONS,
  ANIMATION_ITERATION_OPTIONS,
  ANIMATION_PRESET_LABELS,
  ANIMATION_PRESETS,
  ANIMATION_TIMING_OPTIONS,
  DEFAULT_ANIMATION_STATE,
  animationCss,
  animationKeyframes,
  animationShorthandValue,
  type AnimationPreset,
  type AnimationState,
} from '@/lib/browser-cssanim-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const PREVIEW_NAME = 'cleanorAnimPreview';

export default function BrowserCssAnimationGenerator() {
  const [state, setState] = useState<AnimationState>(DEFAULT_ANIMATION_STATE);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [replayKey, setReplayKey] = useState(0);

  const output = useMemo(() => animationCss(state), [state]);
  const previewKeyframes = useMemo(() => animationKeyframes(state, PREVIEW_NAME), [state]);
  const previewShorthand = useMemo(() => animationShorthandValue(state, PREVIEW_NAME), [state]);

  function update<K extends keyof AnimationState>(key: K, value: AnimationState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
    setReplayKey((k) => k + 1);
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Animation</span>
        <h2 className="tool-live-title">CSS animation generator</h2>
        <p className="tool-live-subtitle">
          Pick a preset, tune the timing, watch it play, and copy the @keyframes plus animation
          shorthand.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Preset</span>
              <select
                onChange={(e) => update('preset', e.target.value as AnimationPreset)}
                value={state.preset}
              >
                {ANIMATION_PRESETS.map((preset) => (
                  <option key={preset} value={preset}>
                    {ANIMATION_PRESET_LABELS[preset]}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Duration: {state.duration}s</span>
              <input
                max={5}
                min={0.2}
                onChange={(e) => update('duration', Number(e.target.value))}
                step={0.1}
                type="range"
                value={state.duration}
              />
            </label>
            <label className="tool-live-field">
              <span>Delay: {state.delay}s</span>
              <input
                max={3}
                min={0}
                onChange={(e) => update('delay', Number(e.target.value))}
                step={0.1}
                type="range"
                value={state.delay}
              />
            </label>
            <label className="tool-live-field">
              <span>Timing function</span>
              <select onChange={(e) => update('timing', e.target.value)} value={state.timing}>
                {ANIMATION_TIMING_OPTIONS.map((timing) => (
                  <option key={timing} value={timing}>
                    {timing}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Iteration count</span>
              <select onChange={(e) => update('iteration', e.target.value)} value={state.iteration}>
                {ANIMATION_ITERATION_OPTIONS.map((iteration) => (
                  <option key={iteration} value={iteration}>
                    {iteration}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Direction</span>
              <select onChange={(e) => update('direction', e.target.value)} value={state.direction}>
                {ANIMATION_DIRECTION_OPTIONS.map((direction) => (
                  <option key={direction} value={direction}>
                    {direction}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
            <button
              className="button button-secondary"
              onClick={() => setReplayKey((k) => k + 1)}
              type="button"
            >
              Replay
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              overflow: 'hidden',
            }}
          >
            <style>{previewKeyframes}</style>
            <div
              key={replayKey}
              style={{
                width: 96,
                height: 96,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #6366f1, #ec4899)',
                animation: previewShorthand,
              }}
            />
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>CSS</span>
            <textarea readOnly rows={12} value={output} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
